/*
 * Pitch.jsx — landing pública para vender Adquiriam a torreras
 * (TOTEM, Cellnex/Abertis, American Tower, Axion).
 *
 * Sin auth. Anima métricas reales del sistema (fetchea /api/pitch/metrics).
 * Enfoque: OPTIMIZACIÓN DE PORTFOLIO (no velocidad de despliegue).
 * CTA final: agendar demo + descargar pitch en PDF.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingDown, Sparkles, ShieldCheck, ScrollText, Wrench, Zap,
  FileText, Building2, ArrowRight, Download, Rocket,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Pitch = () => {
  const [metrics, setMetrics] = useState({ sites: 0, sars: 0, operators: 7, savings_annual_eur: 0 });
  const [animated, setAnimated] = useState({ sites: 0, sars: 0, savings: 0 });

  useEffect(() => {
    fetch(`${BACKEND}/api/pitch/metrics`).then((r) => r.json()).then(setMetrics).catch(() => {});
  }, []);

  // Anima números al montar
  useEffect(() => {
    const dur = 1200;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - t, 3);
      setAnimated({
        sites: Math.round(metrics.sites * ease),
        sars: Math.round(metrics.sars * ease),
        savings: Math.round(metrics.savings_annual_eur * ease),
      });
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [metrics]);

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="pitch-landing">
      {/* Ambient grain + neon glow */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03]"
           style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")` }}/>
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full pointer-events-none"
           style={{ background: 'radial-gradient(circle, rgba(163,255,18,0.15) 0%, transparent 70%)' }}/>

      {/* HEADER */}
      <header className="relative z-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1">
            <span className="text-white font-black text-2xl lowercase tracking-tight">adqui<span className="text-[#A3FF12]">riam</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <a href={`${BACKEND}/api/pitch/pdf`} target="_blank" rel="noreferrer"
              data-testid="download-pitch-pdf"
              className="hidden sm:inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-bold">
              <Download size={14}/> Descargar PDF
            </a>
            <Link to="/login"
              className="bg-[#A3FF12] text-black font-black px-4 py-2 rounded-lg text-sm hover:bg-[#b8ff3a]">
              Ver la app →
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-24 sm:pt-24 sm:pb-32">
        <div className="text-[10px] font-black tracking-[0.25em] text-[#A3FF12] mb-4">PLATAFORMA COMERCIAL 2026 · TORRERAS</div>
        <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight">
          Tu portfolio de<br/>emplazamientos,<br/>
          <span className="text-[#A3FF12]">optimizado.</span>
        </h1>
        <p className="text-lg sm:text-xl text-white/60 mt-6 max-w-2xl leading-relaxed">
          Adquiriam es la única plataforma que combina <span className="text-white font-bold">gestión operativa</span>,
          <span className="text-white font-bold"> renegociación con Adquiriam</span> y
          <span className="text-white font-bold"> palancas normativas</span> para bajar la renta
          de miles de emplazamientos sin romper la relación con el propietario.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <a href="mailto:marco@sitefactory.es?subject=Demo%20Adquiriam"
            className="inline-flex items-center gap-2 bg-[#A3FF12] text-black font-black px-6 py-3 rounded-xl text-base hover:bg-[#b8ff3a] transition-colors">
            <Rocket size={16}/> Solicitar demo · piloto 100 sites
          </a>
          <a href={`${BACKEND}/api/pitch/pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white font-black px-6 py-3 rounded-xl text-base hover:bg-white/10">
            <Download size={16}/> Descargar deck (PDF)
          </a>
        </div>
      </section>

      {/* METRICS */}
      <section className="relative z-10 border-y border-white/5 bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Metric label="Emplazamientos en producción" value={animated.sites.toLocaleString('es-ES')}/>
          <Metric label="Informes SAR generados con Adquiriam" value={animated.sars.toLocaleString('es-ES')}/>
          <Metric label="Operadores con branding integrado" value={metrics.operators}/>
          <Metric label="Ahorro anual firmado (€)" value={`${animated.savings.toLocaleString('es-ES')}`} accent/>
        </div>
      </section>

      {/* MÓDULO ESTRELLA */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20 sm:py-28">
        <div className="text-[10px] font-black tracking-[0.25em] text-[#A3FF12] mb-3">MÓDULO ESTRELLA</div>
        <h2 className="text-3xl sm:text-5xl font-black leading-[1.05] max-w-3xl">
          Bajada de renta con brazo <span className="text-[#A3FF12]">preventivo</span>.
        </h2>
        <p className="text-white/60 text-lg mt-4 max-w-2xl">
          El único módulo del mercado que combina relación humana + palancas normativas + Adquiriam para renegociar sin romper contratos.
        </p>

        <div className="mt-12 grid md:grid-cols-3 gap-4">
          <StepCard n="01" title="Detección automática" desc="El sistema identifica sites con renta > percentil 60 del portfolio y contratos próximos a vencer."/>
          <StepCard n="02" title="Visita preventiva" desc="Programa una intervención de mantenimiento que refuerza el vínculo con el propietario antes de negociar."/>
          <StepCard n="03" title="Propuesta Adquiriam + normativa" desc="Adquiriam escribe la carta usando palancas legales (ascensor, ITE, renovables, accesibilidad) como oferta win-win."/>
        </div>
      </section>

      {/* PALANCAS NORMATIVAS */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20">
        <div className="text-[10px] font-black tracking-[0.25em] text-[#A3FF12] mb-3">PALANCAS NORMATIVAS</div>
        <h2 className="text-3xl sm:text-5xl font-black leading-[1.05] max-w-3xl">
          Convertimos <span className="text-[#A3FF12]">obligación legal</span> del vecino en <span className="text-[#A3FF12]">bajada de renta</span> para ti.
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-3">
          <LeverCard icon={Building2} title="Adaptación de ascensor (RD 88/2013)"
            desc="Financiamos 20-40k€ de modernización. Bajada compensada 3-5 años. ROI 8-15%."/>
          <LeverCard icon={Zap} title="Autoconsumo solar (RD 244/2019)"
            desc="Placas en cubierta. Comunidad + tú os beneficiáis + 60% IRPF + hasta 95% IBI. ROI 10-20%."/>
          <LeverCard icon={ShieldCheck} title="Accesibilidad (Ley 8/2013 art. 15)"
            desc="Rampas y adaptaciones obligatorias vencidas. Financiamos a cambio de 3 años de rebaja."/>
          <LeverCard icon={Wrench} title="Rehabilitación energética (Next Generation)"
            desc="Gestionamos ayudas del 80%. Rebaja de renta 5 años + hasta 60% deducción IRPF."/>
          <LeverCard icon={ScrollText} title="ITE / Informe de Evaluación (Ley 8/2013)"
            desc="Adelantamos IEE + reparaciones críticas para edificios &gt;50 años."/>
          <LeverCard icon={FileText} title="Amianto en cubiertas (RD 396/2006)"
            desc="Sustituimos fibrocemento y modernizamos estructura. Rebaja 4-6 años + ampliación."/>
        </div>
      </section>

      {/* MÓDULOS */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20 border-t border-white/5">
        <div className="text-[10px] font-black tracking-[0.25em] text-[#A3FF12] mb-3">TODO EL CICLO</div>
        <h2 className="text-3xl sm:text-5xl font-black leading-[1.05]">Una app. Cero papel.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          <ModuleCard title="SAR con Adquiriam" desc="Informe legal completo en 90s. Catastro + Wikidata BIC integrados."/>
          <ModuleCard title="Mantenimiento" desc="Preventivo + correctivo. Peritaje Adquiriam de humedades (integración Humedisity)."/>
          <ModuleCard title="Bajada de renta" desc="Detección + Adquiriam + normativa + firma digital + KPI portfolio."/>
          <ModuleCard title="Facturación SII/Verifactu" desc="Trazabilidad completa. Export Sage/Holded. IVA operado en la propia app."/>
          <ModuleCard title="Portal propietario" desc="Link único por site. Ve facturas, comunicaciones y firmas. Reduce fricción."/>
          <ModuleCard title="API abierta" desc="Integra con tu Salesforce/SAP. Webhook para eventos críticos."/>
        </div>
      </section>

      {/* PRICING */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-20 border-t border-white/5">
        <div className="text-[10px] font-black tracking-[0.25em] text-[#A3FF12] mb-3">MODELO ECONÓMICO</div>
        <h2 className="text-3xl sm:text-5xl font-black leading-[1.05]">Pago por site + éxito.</h2>
        <p className="text-white/60 text-lg mt-4 max-w-2xl">Alineamos incentivos. Solo ganamos cuando tú ganas.</p>

        <div className="mt-10 grid md:grid-cols-2 gap-3">
          <PriceCard label="Fee SaaS por site" value="2,50 € / site / mes"
            bullets={["Plataforma ilimitada", "SARs sin límite", "Mantenimiento + agenda + portal", "Soporte SLA 24h"]}/>
          <PriceCard highlight label="Éxito bajada de renta" value="15% del ahorro anual"
            bullets={["Solo si se firma", "Aplicable al 1r año", "Sin coste si no hay ahorro", "Contabilizado en tu portfolio"]}/>
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="text-4xl sm:text-6xl font-black leading-[1.02]">
          Piloto de <span className="text-[#A3FF12]">90 días</span>.<br/>
          100 sites tuyos.<br/>
          Cero riesgo.
        </h2>
        <p className="text-white/60 text-lg mt-6 max-w-xl mx-auto">
          Migración desde Excel + formación + soporte prioritario incluidos. Si en 90 días no ves ROI, cierras el piloto sin coste.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <a href="mailto:marco@sitefactory.es?subject=Piloto%2090%20d%C3%ADas%20Adquiriam"
            data-testid="cta-email"
            className="inline-flex items-center gap-2 bg-[#A3FF12] text-black font-black px-6 py-3 rounded-xl text-base hover:bg-[#b8ff3a]">
            Contactar <ArrowRight size={16}/>
          </a>
          <a href={`${BACKEND}/api/pitch/pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white font-black px-6 py-3 rounded-xl text-base hover:bg-white/10">
            <Download size={16}/> Descargar deck
          </a>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 py-8 text-center text-white/40 text-xs">
        adquiriam · 2026 · pitch comercial · marco@sitefactory.es
      </footer>
    </div>
  );
};

// ============ subcomponents ============
const Metric = ({ label, value, accent }) => (
  <div>
    <div className={`text-4xl sm:text-5xl font-black ${accent ? 'text-[#A3FF12]' : 'text-white'}`}>{value}</div>
    <div className="text-white/50 text-xs sm:text-sm mt-1">{label}</div>
  </div>
);

const StepCard = ({ n, title, desc }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 hover:border-[#A3FF12]/40 transition-colors">
    <div className="text-[#A3FF12] font-black text-xs tracking-widest">{n}</div>
    <div className="text-white font-black text-lg mt-2">{title}</div>
    <div className="text-white/60 text-sm mt-2 leading-relaxed">{desc}</div>
  </div>
);

const LeverCard = ({ icon: Icon, title, desc }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 hover:bg-white/[0.06] transition-colors">
    <Icon className="text-[#A3FF12]" size={18}/>
    <div className="text-white font-black text-sm mt-2">{title}</div>
    <div className="text-white/60 text-xs mt-1 leading-relaxed">{desc}</div>
  </div>
);

const ModuleCard = ({ title, desc }) => (
  <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
    <div className="text-white font-black text-sm">{title}</div>
    <div className="text-white/50 text-xs mt-1 leading-relaxed">{desc}</div>
  </div>
);

const PriceCard = ({ label, value, bullets, highlight }) => (
  <div className={`rounded-2xl p-6 ${highlight ? 'bg-[#A3FF12]/10 border-2 border-[#A3FF12]/40' : 'bg-white/[0.03] border border-white/10'}`}>
    <div className={`text-xs uppercase font-black tracking-wide ${highlight ? 'text-[#A3FF12]' : 'text-white/50'}`}>{label}</div>
    <div className={`text-2xl sm:text-3xl font-black mt-2 ${highlight ? 'text-[#A3FF12]' : 'text-white'}`}>{value}</div>
    <ul className="mt-4 space-y-1.5">
      {bullets.map((b, i) => (
        <li key={i} className="text-white/70 text-sm flex items-center gap-2">
          <span className="text-[#A3FF12]">✓</span> {b}
        </li>
      ))}
    </ul>
  </div>
);

export default Pitch;
